import { AfterContentInit, ChangeDetectorRef, Component, ContentChildren, EventEmitter, HostBinding, Input, OnChanges, OnInit, Output, QueryList, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { ComponentBaseComponent } from '../component-base/component-base.component';
import { TabComponent } from './tab/tab.component';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { debounceTime, Subject, Subscription } from 'rxjs';
import { RememberService } from '@app/core/services/remember.service';

@Component({
    selector: 'tt-tabs',
    templateUrl: './tabs.component.html',
    styleUrls: ['./tabs.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class TabsComponent<TType extends string = ''> extends ComponentBaseComponent implements AfterContentInit, OnInit, OnChanges {
    /**
     * Id used for persisting the last selected id of the tabs using `616`.
     */
    @Input()
    public ttRememberId: string = '';

    /**
     * Whether the tabs should not retrieve the last stored selected id upon initialization and only remember the new selected id.
     *
     * @default true
     */
    @Input()
    public set ttOnlyRemember(value: BooleanInput) {
        this._onlyRemember = coerceBooleanProperty(value);
    }
    public get ttOnlyRemember(): boolean {
        return this._onlyRemember;
    }
    private _onlyRemember = true;

    /**
     * Whether tabs should be dismissable.
     *
     * @default false
     */
    @Input()
    public set ttDismissable(value: BooleanInput) {
        this._dismissable = coerceBooleanProperty(value);
    }
    public get ttDismissable(): boolean {
        return this._dismissable;
    }
    private _dismissable = false;

    @Input()
    public set ttAutoSelectFirst(value: BooleanInput) {
        this._autoSelectFirst = coerceBooleanProperty(value);
    }
    public get ttAutoSelectFirst(): boolean {
        return this._autoSelectFirst;
    }
    private _autoSelectFirst = true;

    @Input()
    public set ttContainedTabs(value: BooleanInput) {
        this._containedTabs = coerceBooleanProperty(value);
    }
    public get ttContainedTabs(): boolean {
        return this._containedTabs;
    }
    private _containedTabs = false;

    /**
     * The id of the selected tab.
     */
    @Input()
    public ttSelectedId: TType = '' as TType;

    /**
     * Event emitted when a new tab is selected. Will also emit an event no initial value is given or when retrieving last stored value from 973.
     */
    @Output()
    public ttSelectedIdChange = new EventEmitter<TType>();

    /**
     * List of the tabs to display.
     */
    @Input()
    public ttTabs: Tab[] = [];

    /**
     * The tabs passed through the content-slot of this tabs component.
     */
    @ContentChildren(TabComponent, { descendants: true })
    public contentTabs?: QueryList<TabComponent<TType>>;

    /**
     * Event emitted when a tab has been attempted to be dismissed. The parent component needs to handle the removal and persistance of this action.
     */
    @Output()
    public ttDismissRequest = new EventEmitter<{ tab: TabComponent<TType>; index: number }>();

    /**
     * Remember subject for remembering the last selected id.
     */
    private rememberSubject = new Subject();

    /**
     * Whether the component is completed loading data.
     */
    public ready = false;

    @HostBinding('class.tt-tabs--contained')
    public get containedTabs() {
        return this.ttContainedTabs;
    }

    constructor(
        private remember: RememberService,
        private cdr: ChangeDetectorRef,
    ) {
        super();
    }

    /**
     * Handles on click event for a tab. `ttModelChange` will be emitted before onClick handler on tab runs.
     *
     * @param tab the tab to perform click event on.
     */
    public onClick(tab: Tab): void {
        this.selectId(tab.id as TType);
        this.rememberSubject.next(this.ttSelectedId);

        if (tab && tab?.onClick && tab.onClick instanceof Function) {
            tab?.onClick();
        }
    }

    /**
     * Sets the given id as the selected id and emits a selected id change event.
     *
     * @emits `ttSelectedIdChange` containing the new id in the event.
     */
    private selectId(id: TType) {
        if (id !== this.ttSelectedId) {
            this.ttSelectedId = id;
            this.ttSelectedIdChange.emit(this.ttSelectedId);
        }

        this.updateActivePropForContentTabs();
    }

    /**
     * Checks if the any of the tabs passed through `ttTabs` property or through slot has the id which matched the current selected id.
     *
     * @returns `true` if any of the `ttTabs` or content-slot tabs has an id matching the currently selected id, `false` if not.
     */
    private hasSelectedTab() {
        const selectedContentTab = this.contentTabs?.find((tab) => !tab.ttDisabled && tab.ttId === this.ttSelectedId);
        const selectedModelTab = this.ttTabs.find((tab) => tab.disabled !== true && tab.disabled !== 'hidden' && tab.id === this.ttSelectedId);

        return !!selectedContentTab || !!selectedModelTab;
    }

    /**
     * Updates the active property for the tabs passed throught in the slot of this tabs component.
     */
    private updateActivePropForContentTabs() {
        this.contentTabs?.forEach((tab) => (tab.ttActive = tab.ttId === this.ttSelectedId));
    }

    /**
     * Selects the first available id of the tabs available. Checks tabs passed through `ttTabs` property before checking tabs passed through content.
     */
    private selectFirstAvailableId() {
        let firstAvailableId: string = '';

        if (this.ttTabs.length > 0) {
            firstAvailableId = this.ttTabs.find((tab) => tab.disabled !== true && tab.disabled !== 'hidden')?.id || '';
        }

        if (!firstAvailableId && !!this.contentTabs && this.contentTabs.length > 0) {
            firstAvailableId = this.contentTabs.find((tab) => tab.ttDisabled !== true)?.ttId || '';
        }

        if (!!firstAvailableId) {
            this.selectId(firstAvailableId as TType);
        }
    }

    /**
     * Remembers the currenlty selected id given that an id is selected and remember id provided.
     */
    private rememberSelectedId() {
        if (!!this.ttRememberId && !!this.ttSelectedId) {
            this.remember.remember(this.ttRememberId!, this.ttSelectedId);
        }
    }

    /**
     * Retrieves the last persisted id of this tabs with the remember id passed through `ttRememberId`.
     */
    private async getLastSelectedId() {
        try {
            this.contentTabs?.forEach((tab) => (tab.ttSkeleton = true));
            const lastId = (await this.remember.getLastStatus(this.ttRememberId))?.[0]?.variablevalue;

            if (!!lastId) {
                this.selectId(lastId);
            }
        } catch (err) {
            console.error(err);
        }
    }

    /**
     * Sets the tab-components of this tabs components as ready.
     */
    private setTabsAsReady() {
        this.contentTabs?.forEach((tab) => (tab.ttSkeleton = false));
        this.ready = true;
    }

    async ngOnInit(): Promise<void> {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['ttAutoSelectFirst']) {
            this.ttAutoSelectFirst = changes['ttAutoSelectFirst'].currentValue;
        }

        if (changes['ttContainedTabs']) {
            this.ttContainedTabs = changes['ttContainedTabs'].currentValue;
        }

        if (changes['ttDismissable']) {
            this.ttDismissable = changes['ttDismissable'].currentValue;

            this.contentTabs?.forEach((tab) => (tab.parentDismissable = this.ttDismissable));
        }

        if (changes?.['ttRememberId']?.currentValue && changes?.['ttRememberId']?.currentValue !== changes?.['ttRememberId']?.previousValue) {
            this.rememberSubject.pipe(debounceTime(500)).subscribe({ next: () => this.rememberSelectedId() });
        }

        if (changes['ttSelectedId']) {
            this.ttSelectedId = changes['ttSelectedId'].currentValue;

            if (!this.hasSelectedTab() && this.ttAutoSelectFirst) {
                this.selectFirstAvailableId();
            }

            this.updateActivePropForContentTabs();
        }
    }

    private clickSubscriptions: Subscription[] = [];
    private dismissSubscriptions: Subscription[] = [];

    private setupClickSubscriptions(tabs: QueryList<TabComponent<TType>> | undefined) {
        if (!tabs) return;

        for (let sub of this.clickSubscriptions) {
            sub.unsubscribe();
        }

        for (let sub of this.dismissSubscriptions) {
            sub.unsubscribe();
        }

        this.clickSubscriptions = [];
        this.dismissSubscriptions = [];

        for (let tab of tabs.toArray()) {
            let index = tabs.toArray().indexOf(tab);
            this.clickSubscriptions.push(tab.ttClick.subscribe((_) => this.onContentTabClick(tab)));
            this.dismissSubscriptions.push(tab.ttDismiss.subscribe((_) => this.ttDismissRequest.emit({ tab: tab, index: index })));
        }
    }

    private onContentTabClick = (tab: TabComponent<TType>) => {
        this.selectId(tab.ttId as TType);
        this.rememberSubject?.next(this.ttSelectedId);
    };

    async ngAfterContentInit(): Promise<void> {
        this.setupClickSubscriptions(this.contentTabs);

        this.contentTabs?.changes.subscribe({
            next: (tabs: QueryList<TabComponent<TType>>) => {
                this.setupClickSubscriptions(tabs);

                tabs.forEach((tab) => (tab.parentDismissable = this.ttDismissable));

                if (!this.hasSelectedTab() && this.ttAutoSelectFirst) {
                    this.selectFirstAvailableId();
                }

                this.updateActivePropForContentTabs();
            },
        });

        this.contentTabs?.forEach((tab) => (tab.parentDismissable = this.ttDismissable));

        if (!this.ttOnlyRemember && !!this.ttRememberId) {
            await this.getLastSelectedId();
        }

        if (!this.hasSelectedTab() && this.ttAutoSelectFirst) {
            this.selectFirstAvailableId();
        }

        this.updateActivePropForContentTabs();

        this.setTabsAsReady();
    }
}

/**
 * Represents the configuration of a single tab.
 */
export interface Tab {
    /**
     * The id of the tab.
     */
    id: string;

    /**
     * The label of the tab (will be translated).
     */
    label?: string;

    /**
     * Optional click handler of the tab, will fire after `ttModelChanged`.
     */
    onClick?: () => any;

    /**
     * Whether the button is disabled, or hidden.
     */
    disabled?: boolean | 'hidden';

    /**
     * Whether the label should be translated, default is `true`.
     */
    translateLabel?: boolean;

    /**
     * Icon to display in the tab.
     */
    icon?: string;
}
